/**
 * Runtime theme switcher — overrides the root CSS variables defined in
 * `index.css` so the ENTIRE token-driven app instantly adopts a new palette
 * without a page reload.
 *
 * Themes adjust the semantic tokens (--primary, --accent, --ring, --success,
 * --warning, --info, --error) via HSL strings.  Neutrals stay stable so
 * existing layouts don't break.
 */

// ─── Hex → HSL conversion (for custom themes) ───────────────────────────
function hexToHsl(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return {
    h: Math.round(hue * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

// Build a full palette variable map from a single primary hex.
export function palettteFromHex(hex) {
  const { h, s, l } = hexToHsl(hex);
  // Clamp saturation/lightness to usable ranges
  const primS = Math.max(40, Math.min(95, s));
  const primL = Math.max(25, Math.min(55, l));
  const accentS = Math.max(25, primS - 10);
  return {
    '--primary': `${h} ${primS}% ${primL}%`,
    '--primary-foreground': primL > 55 ? `${h} 40% 10%` : `${h} 15% 98%`,
    '--secondary': `${(h + 25) % 360} ${primS}% ${primL}%`,
    '--secondary-foreground': `${h} 15% 98%`,
    '--accent': `${h} ${accentS}% 95%`,
    '--accent-foreground': `${h} ${primS}% 28%`,
    '--ring': `${h} ${primS}% ${primL}%`,
  };
}

export const PALETTES = {
  default: {
    label: 'Emerald',
    swatch: '#13815c',
    vars: {},  // root defaults (no override)
  },
  midnight: {
    label: 'Midnight',
    swatch: '#0f172a',
    vars: {
      '--background': '222 47% 6%',
      '--foreground': '210 20% 92%',
      '--card': '222 40% 10%',
      '--card-foreground': '210 20% 92%',
      '--popover': '222 40% 10%',
      '--popover-foreground': '210 20% 92%',
      '--primary': '160 84% 42%',
      '--primary-foreground': '222 47% 6%',
      '--secondary': '38 92% 55%',
      '--secondary-foreground': '222 47% 6%',
      '--muted': '222 30% 16%',
      '--muted-foreground': '215 16% 65%',
      '--accent': '160 60% 18%',
      '--accent-foreground': '160 84% 72%',
      '--border': '222 25% 20%',
      '--input': '222 25% 20%',
      '--ring': '160 84% 42%',
      '--success': '160 84% 45%',
      '--warning': '38 92% 58%',
      '--error': '346 87% 55%',
      '--info': '199 89% 55%',
    },
  },
  sapphire: {
    label: 'Sapphire',
    swatch: '#2563eb',
    vars: {
      '--primary': '221 83% 53%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '262 83% 58%',
      '--secondary-foreground': '210 40% 98%',
      '--accent': '221 70% 96%',
      '--accent-foreground': '221 83% 30%',
      '--ring': '221 83% 53%',
      '--info': '221 83% 53%',
    },
  },
  crimson: {
    label: 'Crimson',
    swatch: '#be123c',
    vars: {
      '--primary': '346 87% 43%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '24 95% 53%',
      '--secondary-foreground': '210 40% 98%',
      '--accent': '346 60% 95%',
      '--accent-foreground': '346 87% 30%',
      '--ring': '346 87% 43%',
    },
  },
  desert: {
    label: 'Desert',
    swatch: '#b45309',
    vars: {
      '--primary': '31 91% 35%',
      '--primary-foreground': '48 100% 97%',
      '--secondary': '43 96% 48%',
      '--secondary-foreground': '31 91% 15%',
      '--accent': '43 96% 92%',
      '--accent-foreground': '31 91% 25%',
      '--ring': '31 91% 35%',
    },
  },
};

const STORAGE_KEY = 'smarto_theme_v1';
const CUSTOM_HEX_KEY = 'smarto_theme_custom_hex_v1';

export function applyTheme(name, options = {}) {
  let theme;
  if (name === 'custom') {
    const hex = options.hex || getSavedCustomHex();
    theme = { vars: palettteFromHex(hex) };
    try { localStorage.setItem(CUSTOM_HEX_KEY, hex); } catch { /* ignore */ }
  } else {
    theme = PALETTES[name] || PALETTES.default;
  }
  const root = document.documentElement;

  // Reset previously-applied overrides by removing inline props we set
  // (browsers keep the stylesheet values underneath).
  const allVars = new Set();
  Object.values(PALETTES).forEach((p) => Object.keys(p.vars).forEach((k) => allVars.add(k)));
  // Also clear any custom-specific keys in case user toggles between custom hexes
  ['--primary', '--primary-foreground', '--secondary', '--secondary-foreground', '--accent', '--accent-foreground', '--ring'].forEach((k) => allVars.add(k));
  allVars.forEach((k) => root.style.removeProperty(k));

  // Apply new theme vars
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));

  // Toggle .dark class for Midnight (Tailwind dark: variants)
  if (name === 'midnight') root.classList.add('dark');
  else root.classList.remove('dark');

  try {
    localStorage.setItem(STORAGE_KEY, name);
  } catch {
    /* quota / incognito — ignore */
  }
  return name;
}

export function getSavedTheme() {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t === 'custom') return 'custom';
    return PALETTES[t] ? t : 'default';
  } catch {
    return 'default';
  }
}

export function getSavedCustomHex() {
  try {
    return localStorage.getItem(CUSTOM_HEX_KEY) || '#13815c';
  } catch {
    return '#13815c';
  }
}

// Apply once on module import so every page (not just Dashboard) picks up the theme
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const saved = getSavedTheme();
  if (saved === 'custom') applyTheme('custom', { hex: getSavedCustomHex() });
  else applyTheme(saved);
}
